import {
  Button,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  Collapse,
  IconButton,
  Box,
  useTheme,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useEffect, useState, useContext } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/InfoContext";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import useUserClaims from "../../hooks/useUserClaims";
import {
  getOrderTypes,
  editOrderTypes,
} from "../../api/services/orderTypes/orderTypes";

const OrderTypeManager = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { openSnackbar } = useSnackbar();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const userClaims = useUserClaims(user);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [newOrderTypes, setNewOrderTypes] = useState({});
  const [newMeasurements, setNewMeasurements] = useState({});
  const [error, setError] = useState({});
  const [editMode, setEditMode] = useState({});
  const [editNames, setEditNames] = useState({});
  const [editMeasurements, setEditMeasurements] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});

  // Fetch categories and order types on component mount
  useEffect(() => {
    const fetchOrderTypes = async () => {
      try {
        const categoriesData = await getOrderTypes();
        setCategories(categoriesData || []);
        const initialExpanded = categoriesData.reduce(
          (acc, cat) => ({ ...acc, [cat.name]: false }),
          {}
        );
        setExpandedCategories(initialExpanded);
      } catch (error) {
        console.error("Error fetching order types:", error);
        openSnackbar("Failed to fetch order types. Please try again.", "error");
      }
    };

    fetchOrderTypes();
  }, [openSnackbar]);

  // Toggle category collapse
  const handleToggleCategory = (categoryName) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  };

  // Handle input change for new category, order type, and measurement
  const handleInputChange = (field, value, categoryName = null) => {
    if (field === "category") {
      setNewCategory(value);
      setError((prev) => ({ ...prev, category: "" }));
    } else if (field === "name" && categoryName) {
      setNewOrderTypes((prev) => ({ ...prev, [categoryName]: value }));
      setError((prev) => ({ ...prev, [categoryName]: "" }));
    } else if (field === "measurement" && categoryName) {
      setNewMeasurements((prev) => ({ ...prev, [categoryName]: value }));
      setError((prev) => ({ ...prev, [categoryName]: "" }));
    }
  };

  // Handle adding a new category
  const handleAddCategory = async () => {
    const trimmedCategory = newCategory.trim().toLowerCase();
    if (!trimmedCategory) {
      setError((prev) => ({
        ...prev,
        category: "Category name cannot be empty",
      }));
      return;
    }
    if (categories.some((cat) => cat.name === trimmedCategory)) {
      setError((prev) => ({ ...prev, category: "Category already exists" }));
      return;
    }

    setIsSubmitting(true);
    try {
      const updatedCategories = [
        ...categories,
        { name: trimmedCategory, orderTypes: [] },
      ];
      await editOrderTypes(user, { categories: updatedCategories });
      setCategories(updatedCategories);
      setNewCategory("");
      setExpandedCategories((prev) => ({ ...prev, [trimmedCategory]: true }));
      openSnackbar("Category added successfully!", "success");
    } catch (error) {
      openSnackbar(
        error.message || "Failed to add category. Please try again.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle adding a new order type to a category
  const handleAddOrderType = async (categoryName) => {
    const trimmedType = newOrderTypes[categoryName]?.trim().toLowerCase() || "";
    const trimmedMeasurement =
      newMeasurements[categoryName]?.trim().toLowerCase() || "";

    if (!trimmedType) {
      setError((prev) => ({
        ...prev,
        [categoryName]: "Order type cannot be empty",
      }));
      return;
    }
    if (!trimmedMeasurement) {
      setError((prev) => ({
        ...prev,
        [categoryName]: "Measurement is required",
      }));
      return;
    }

    const categoryExists = categories.find((cat) => cat.name === categoryName);
    if (!categoryExists) {
      setError((prev) => ({
        ...prev,
        [categoryName]: "Category does not exist",
      }));
      return;
    }
    if (categoryExists.orderTypes.some((type) => type.name === trimmedType)) {
      setError((prev) => ({
        ...prev,
        [categoryName]: "Order type already exists in this category",
      }));
      return;
    }

    setIsSubmitting(true);
    try {
      const updatedCategories = categories.map((cat) =>
        cat.name === categoryName
          ? {
              ...cat,
              orderTypes: [
                ...cat.orderTypes,
                {
                  name: trimmedType,
                  measurement: trimmedMeasurement,
                },
              ],
            }
          : cat
      );
      await editOrderTypes(user, { categories: updatedCategories });
      setCategories(updatedCategories);
      setNewOrderTypes((prev) => ({ ...prev, [categoryName]: "" }));
      setNewMeasurements((prev) => ({ ...prev, [categoryName]: "" }));
      openSnackbar("Order type added successfully!", "success");
    } catch (error) {
      openSnackbar(
        error.message || "Failed to add order type. Please try again.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit button click
  const handleEditButtonClick = (categoryName, typeName) => {
    setEditMode((prev) => ({
      ...prev,
      [`${categoryName}_${typeName}`]: true,
    }));
    setEditNames((prev) => ({
      ...prev,
      [`${categoryName}_${typeName}`]: typeName,
    }));
    setEditMeasurements((prev) => ({
      ...prev,
      [`${categoryName}_${typeName}`]: categories
        .find((cat) => cat.name === categoryName)
        .orderTypes.find((t) => t.name === typeName).measurement,
    }));
  };

  // Handle save button click for editing or removing
  const handleSaveButtonClick = async (categoryName, typeName, action) => {
    setIsSubmitting(true);
    setEditMode((prev) => ({
      ...prev,
      [`${categoryName}_${typeName}`]: false,
    }));

    try {
      let updatedCategories;
      if (action === "remove") {
        updatedCategories = categories.map((cat) =>
          cat.name === categoryName
            ? {
                ...cat,
                orderTypes: cat.orderTypes.filter((t) => t.name !== typeName),
              }
            : cat
        );
      } else {
        const trimmedNewName = editNames[`${categoryName}_${typeName}`]
          ?.trim()
          .toLowerCase();
        const trimmedMeasurement = editMeasurements[
          `${categoryName}_${typeName}`
        ]
          ?.trim()
          .toLowerCase();

        if (!trimmedNewName) {
          openSnackbar("Order type name cannot be empty", "error");
          setIsSubmitting(false);
          return;
        }
        if (!trimmedMeasurement) {
          openSnackbar("Measurement is required", "error");
          setIsSubmitting(false);
          return;
        }

        const category = categories.find((cat) => cat.name === categoryName);
        if (
          trimmedNewName !== typeName &&
          category.orderTypes.some((t) => t.name === trimmedNewName)
        ) {
          openSnackbar(
            "Order type name already exists in this category",
            "error"
          );
          setIsSubmitting(false);
          return;
        }

        updatedCategories = categories.map((cat) =>
          cat.name === categoryName
            ? {
                ...cat,
                orderTypes: cat.orderTypes.map((t) =>
                  t.name === typeName
                    ? {
                        name: trimmedNewName,
                        measurement: trimmedMeasurement,
                      }
                    : t
                ),
              }
            : cat
        );
      }

      await editOrderTypes(user, { categories: updatedCategories });
      setCategories(updatedCategories);
      openSnackbar(
        action === "remove"
          ? "Order type removed successfully!"
          : "Order type updated successfully!",
        "success"
      );
    } catch (error) {
      openSnackbar(
        error.message ||
          (action === "remove"
            ? "Failed to remove order type."
            : "Failed to update order type."),
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes during edit
  const handleEditInputChange = (categoryName, typeName, field, value) => {
    const key = `${categoryName}_${typeName}`;
    if (field === "name") {
      setEditNames((prev) => ({ ...prev, [key]: value }));
    } else {
      setEditMeasurements((prev) => ({ ...prev, [key]: value }));
    }
  };

  return (
    <Box
      sx={{
        padding: "20px",
        margin: "20px",
        backgroundColor: theme.palette.background.alt,
        color: theme.palette.secondary[300],
        borderRadius: "4px",
        boxShadow: theme.shadows[5],
      }}
    >
      <Typography
        variant="h4"
        align="center"
        sx={{ fontWeight: "bold", marginBottom: "20px" }}
      >
        Manage Order Types
      </Typography>
      <List>
        {categories.map((category) => (
          <div key={category.name}>
            <ListItem
              sx={{ paddingLeft: 0 }}
              secondaryAction={
                <IconButton
                  onClick={() => handleToggleCategory(category.name)}
                  edge="end"
                >
                  {expandedCategories[category.name] ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )}
                </IconButton>
              }
            >
              <ListItemText
                primary={
                  <Typography variant="h6">
                    {category.name.charAt(0).toUpperCase() +
                      category.name.slice(1)}
                  </Typography>
                }
              />
            </ListItem>
            <Collapse in={expandedCategories[category.name]} timeout="auto">
              <List component="div" disablePadding>
                {category.orderTypes.map((type) => (
                  <ListItem key={type.name} sx={{ pl: 4 }}>
                    {editMode[`${category.name}_${type.name}`] ? (
                      <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                        <TextField
                          label="Order Type Name"
                          value={
                            editNames[`${category.name}_${type.name}`] || ""
                          }
                          onChange={(e) =>
                            handleEditInputChange(
                              category.name,
                              type.name,
                              "name",
                              e.target.value
                            )
                          }
                          size="small"
                          sx={{ flex: 1 }}
                          disabled={
                            !(userClaims.superAdmin || userClaims.admin)
                          }
                        />
                        <TextField
                          label="Measurement"
                          value={
                            editMeasurements[`${category.name}_${type.name}`] ||
                            ""
                          }
                          onChange={(e) =>
                            handleEditInputChange(
                              category.name,
                              type.name,
                              "measurement",
                              e.target.value
                            )
                          }
                          size="small"
                          sx={{ flex: 1 }}
                          disabled={
                            !(userClaims.superAdmin || userClaims.admin)
                          }
                        />
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() =>
                            handleSaveButtonClick(
                              category.name,
                              type.name,
                              "update"
                            )
                          }
                          disabled={
                            !(userClaims.superAdmin || userClaims.admin)
                          }
                        >
                          Save
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() =>
                            handleSaveButtonClick(
                              category.name,
                              type.name,
                              "remove"
                            )
                          }
                          disabled={
                            !(userClaims.superAdmin || userClaims.admin)
                          }
                        >
                          Delete
                        </Button>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          width: "100%",
                          alignItems: "center",
                        }}
                      >
                        <ListItemText
                          primary={
                            type.name.charAt(0).toUpperCase() +
                            type.name.slice(1)
                          }
                          secondary={`Measurement: ${type.measurement}`}
                          sx={{ flex: "1 1 auto" }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() =>
                            handleEditButtonClick(category.name, type.name)
                          }
                          disabled={
                            !(userClaims.superAdmin || userClaims.admin)
                          }
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() =>
                            handleSaveButtonClick(
                              category.name,
                              type.name,
                              "remove"
                            )
                          }
                          disabled={
                            !(userClaims.superAdmin || userClaims.admin)
                          }
                        >
                          Delete
                        </Button>
                      </Box>
                    )}
                  </ListItem>
                ))}
                {(userClaims.superAdmin || userClaims.admin) &&
 (
                  <ListItem sx={{ pl: 4 }}>
                    <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                      <TextField
                        label="New Order Type"
                        value={newOrderTypes[category.name] || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "name",
                            e.target.value,
                            category.name
                          )
                        }
                        size="small"
                        sx={{ flex: 1 }}
                        error={
                          !!error[category.name] &&
                          error[category.name].includes("Order type")
                        }
                        helperText={
                          error[category.name]?.includes("Order type")
                            ? error[category.name]
                            : ""
                        }
                        disabled={!(userClaims.superAdmin || userClaims.admin)}
                      />
                      <TextField
                        label="Measurement"
                        value={newMeasurements[category.name] || ""}
                        onChange={(e) =>
                          handleInputChange(
                            "measurement",
                            e.target.value,
                            category.name
                          )
                        }
                        size="small"
                        sx={{ flex: 1 }}
                        error={
                          !!error[category.name] &&
                          error[category.name].includes("Measurement")
                        }
                        helperText={
                          error[category.name]?.includes("Measurement")
                            ? error[category.name]
                            : ""
                        }
                        disabled={!(userClaims.superAdmin || userClaims.admin)}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleAddOrderType(category.name)}
                        disabled={!(userClaims.superAdmin || userClaims.admin)}
                      >
                        Add Order
                      </Button>
                    </Box>
                  </ListItem>
                )}
              </List>
            </Collapse>
          </div>
        ))}
      </List>
      {(userClaims.superAdmin || userClaims.admin) && (
        <Box sx={{ display: "flex", gap: 2, marginTop: "20px" }}>
          <TextField
            label="New Category"
            value={newCategory}
            onChange={(e) => handleInputChange("category", e.target.value)}
            fullWidth
            error={!!error.category}
            helperText={error.category || ""}
            disabled={!(userClaims.superAdmin || userClaims.admin)}
          />
          <Button
            variant="contained"
            onClick={handleAddCategory}
            disabled={!(userClaims.superAdmin || userClaims.admin)}
            sx={{ minWidth: "150px" }}
          >
            Add Category
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default OrderTypeManager;
